import { useEffect, useRef, useCallback } from 'react';
import type { AppStore } from '../stores/createStore';
import { useAppSelector } from '../stores/hooks';
import { refreshAccessToken, logout } from '../stores/slices/authSlice';

/**
 * tRPC React hooks 类型
 */
interface TRPCReact {
  auth: {
    refresh: {
      useMutation: (options?: {
        onSuccess?: (data: { accessToken: string; refreshToken?: string }) => void;
        onError?: () => void;
      }) => {
        mutate: (input: { refreshToken: string }) => void;
      };
    };
  };
}

/**
 * TokenRefreshHandler 配置
 */
export interface TokenRefreshHandlerProps {
  store: AppStore;
  trpc: TRPCReact;
}

/**
 * Token 自动刷新服务
 * 在 token 即将过期时自动刷新
 * 
 * 使用方式：在 Providers 中全局挂载，无需子组件
 */
export function TokenRefreshHandler({ store, trpc }: TokenRefreshHandlerProps) {
  const token = useAppSelector((state) => state.auth.token);
  const refreshTokenValue = useAppSelector((state) => state.auth.refreshToken);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  // 使用 tRPC 调用刷新接口
  const refreshMutation = trpc.auth.refresh.useMutation({
    onSuccess: (data) => {
      // 刷新成功，更新 token
      store.dispatch(
        refreshAccessToken({
          token: data.accessToken,
          refreshToken: data.refreshToken,
        })
      );
      isRefreshingRef.current = false;
    },
    onError: () => {
      // 刷新失败，清除认证状态
      store.dispatch(logout());
      isRefreshingRef.current = false;
    },
  });

  // 检查并刷新 token
  const checkAndRefresh = useCallback(() => {
    if (isRefreshingRef.current || !refreshTokenValue || !token) {
      return;
    }

    try {
      // 解析 JWT token（不验证签名，只读取过期时间）
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // 转换为毫秒
      const now = Date.now();
      const timeUntilExpiry = exp - now;

      // 如果 token 在 5 分钟内过期，或者已经过期，则刷新
      if (timeUntilExpiry < 5 * 60 * 1000) {
        isRefreshingRef.current = true;
        refreshMutation.mutate({ refreshToken: refreshTokenValue });
      }
    } catch (error) {
      // 解析失败，如果已认证且有 refresh token，尝试刷新
      if (isAuthenticated && refreshTokenValue) {
        isRefreshingRef.current = true;
        refreshMutation.mutate({ refreshToken: refreshTokenValue });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshTokenValue, isAuthenticated]);

  // 设置定时检查
  useEffect(() => {
    if (isAuthenticated && token && refreshTokenValue) {
      // 立即检查一次
      checkAndRefresh();

      // 每 1 分钟检查一次
      refreshIntervalRef.current = setInterval(() => {
        checkAndRefresh();
      }, 60 * 1000);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isAuthenticated, token, refreshTokenValue, checkAndRefresh]);

  return null;
}

